<template>
  <div class="">
    <div
      v-show="toolbarVisible && !panning" ref="toolbarRef" class="absolute z-[1000] flex bg-white rounded-sm shadow-whiteboard-obj-prop p-1 space-x-1"
      :style="{ top: `${toolbarPos.y}px`, left: `${toolbarPos.x}px` }" @wheel.ctrl.prevent @mousemove.prevent
    >
      <div v-for="(p, k) in editableProps" :key="k" class="flex items-center">
        <div
          v-if="p.type === 'border'" v-tooltip="{ placement: 'top', text: p.name }" class="rounded-full w-6 h-6 m-1.5 cursor-pointer border"
          :style="{ borderColor: form.borderColor === 'transparent' ? '#9cafa3' : form.borderColor === '#FFFFFF' ? '#b2b2b2' : form.borderColor, borderWidth: `${form.borderThickness / 5}px` }" @click="onOpenSelection($event, p.type)"
        >
          <div v-if=" form.borderColor === 'transparent'" class="absolute rotate-45 top-[11px] h-[1px] bg-gray-400" :style="{ width: `${23 / (form.borderThickness * 0.1)}px` }" />
        </div>
        <div
          v-else-if="p.type === 'backgroundColor'" v-tooltip="{ placement: 'top', text: p.name }" class="rounded-full w-6 h-6 m-1.5 cursor-pointer border"
          :style="{ backgroundColor: form.backgroundColor, borderColor: utils.darkenColor(form.backgroundColor, 0.3) }" @click="onOpenSelection($event, p.type)"
        >
          <div v-if="form.backgroundColor === 'transparent'" class="absolute rotate-45 top-[21px] w-[24px] h-[1px] bg-gray-400" />
        </div>
        <div
          v-else-if="p.type === 'color'" v-tooltip="{ placement: 'top', text: p.name }" class="rounded-full w-6 h-6 m-1.5 cursor-pointer border"
          :style="{ backgroundColor: form.color, borderColor: utils.darkenColor(form.color, 0.3) }" @click="onOpenSelection($event, p.type)"
        >
          <div v-if="form.color === 'transparent'" class="absolute rotate-45 top-[11px] w-[23px] h-[1px] bg-gray-400" />
        </div>
        <div
          v-else-if="p.type === 'font'" v-tooltip="{ placement: 'top', text: p.name }" class="w-auto m-1.5 text-base font-bold cursor-pointer"
          @click="onOpenSelection($event, p.type)" v-text="form.font"
        />
        <div
          v-else-if="p.type === 'fontSize'" v-tooltip="{ placement: 'top', text: p.name }" class="w-auto m-1.5 text-base font-bold cursor-pointer"
          @click="onOpenSelection($event, p.type)" v-text="availableFontSizes[form.fontSize] || form.fontSize"
        />
        <div
          v-else-if="p.type === 'textColor'" v-tooltip="{ placement: 'top', text: p.name }" class="w-6 text-center text-base font-bold m-1.5 cursor-pointer"
          :style="{ borderBottom: `5px solid ${form.textColor}` }" @click="onOpenSelection($event, p.type)" v-text="'A'"
        />
        <div v-else-if="p.type === 'textAlign'" v-tooltip="{ placement: 'top', text: p.name }" class="w-6 h-6 m-1.5 cursor-pointer">
          <font-awesome-icon class="w-6 h-6" :icon="availableTextAligns[form.textAlign]" @click="onOpenSelection($event, p.type)" />
        </div>
        <div
          v-else-if="p.type === 'fontStyle'" v-tooltip="{ placement: 'top', text: p.name }" class="w-6 border-b-4 border-black italic text-center text-base font-bold m-1.5 cursor-pointer"
          @click="onOpenSelection($event, p.type)" v-text="'B'"
        />
        <div
          v-else-if="p.type === 'size'" v-tooltip="{ placement: 'top', text: p.name }" class="w-auto m-1.5 text-base font-bold cursor-pointer"
          @click="onOpenSelection($event, p.type)" v-text="form.size"
        />
        <div
          v-else-if="p.type === 'shapeType'" v-tooltip="{ placement: 'top', text: p.name }" class="w-6 h-6 m-1.5 cursor-pointer"
          @click="onOpenSelection($event, p.type)"
        >
          <svg viewBox="0 0 50 50">
            <path :style="{ fill: 'none', stroke: '#000000', strokeWidth: '3px' }" :d="availableShapes[form.shapeType]" />
          </svg>
        </div>
        <div
          v-else-if="p.type === 'frameSize'" v-tooltip="{ placement: 'top', text: p.name }" class="w-auto m-1.5 text-base font-bold cursor-pointer"
          @click="onOpenSelection($event, p.type)" v-text="utils.capitalizeFirstLetter(form.frameSize)"
        />
        <div v-else-if="p.type === 'lock' && !form.lock" v-tooltip="{ placement: 'top', text: p.name }" class="w-6 h-6 m-1.5 cursor-pointer">
          <font-awesome-icon class="w-6 h-6" icon="fa-light fa-lock-open" @click="onInputChange('lock', { lock: !form.lock }, 'lock', !form.lock)" />
        </div>
        <div v-else-if="p.type === 'lock' && form.lock && !form.preventUnlock && !isReadOnly" v-tooltip="{ placement: 'top', text: 'Unlock' }" class="w-6 h-6 m-1.5 cursor-pointer">
          <font-awesome-icon class="w-6 h-6" icon="fa-light fa-lock" @click="onInputChange('lock', { lock: !form.lock }, 'lock', !form.lock)" />
        </div>
        <div
          v-else-if="p.type === 'imageType'" v-tooltip="{ placement: 'top', text: p.name }" class="w-auto m-1.5 text-base font-bold cursor-pointer"
          @click="onOpenSelection($event, p.type)" v-text="getImageTypeText"
        />
        <div
          v-else-if="p.type === 'scale'" v-tooltip="{ placement: 'top', text: p.name }" class="w-auto m-1.5 text-base font-bold cursor-pointer"
          @click="onOpenSelection($event, p.type)" v-text="`${form.scale}%`"
        />
        <div v-else-if="p.type === 'addModelDetails'" v-tooltip="{ placement: 'top', text: p.name }" class="w-6 h-6 m-1.5 cursor-pointer">
          <font-awesome-icon class="w-6 h-6" icon="fa-light fa-plus" @click="onAddModelDetails" />
        </div>
        <div v-else-if="p.type === 'addArticleDetails'" v-tooltip="{ placement: 'top', text: p.name }" class="w-6 h-6 m-1.5 cursor-pointer">
          <font-awesome-icon class="w-6 h-6" icon="fa-light fa-plus" @click="onAddArticleDetails" />
        </div>
        <div
          v-else-if="p.type === 'showLabels'" class="m-1.5 text-base cursor-pointer" :class="{ 'text-primary-500': form.showLabels }"
          @click="onInputChange('showLabels', { showLabels: !form.showLabels }, 'showLabels', !form.showLabels)" v-text="p.name"
        />
        <div v-else-if="p.type === 'attributes'" v-tooltip="p.name" class="w-6 h-6 m-1.5 cursor-pointer">
          <font-awesome-icon class="w-6 h-6" icon="fa-light fa-table" @click="onOpenAttributesSelector" />
        </div>
        <div v-else-if="p.type === 'addThumbnailAttributes'" v-tooltip="p.name" class="w-6 h-6 m-1.5 cursor-pointer">
          <font-awesome-icon class="w-6 h-6" icon="fa-light fa-table" @click="onOpenThumbnailAttributesSelector" />
        </div>
        <div v-else-if="p.type === 'assignImage' && allowExternalImageToArticle" v-tooltip="p.name" class="w-6 h-6 m-1.5 cursor-pointer">
          <font-awesome-icon class="w-6 h-6" icon="fa-light fa-file-image" @click="onAssignImage" />
        </div>
        <div v-else-if="p.type === 'deleteImage' && allowDeleteImage" v-tooltip="p.name" class="w-6 h-6 m-1.5 cursor-pointer">
          <font-awesome-icon class="w-6 h-6" icon="fa-light fa-trash-can" @click="onDeleteImage" />
        </div>
        <div
          v-else-if="p.type === 'showResources'" class="m-1.5 text-base cursor-pointer" :class="{ 'text-primary-500': form.showResources }"
          @click="onInputChange('showResources', { showResources: !form.showResources }, 'showResources', !form.showResources)" v-text="p.name"
        />
        <div
          v-else-if="p.type === 'showDesignOptions'" class="m-1.5 text-base cursor-pointer" :class="{ 'text-primary-500': form.showDesignOptions }"
          @click="onInputChange('showDesignOptions', { showDesignOptions: !form.showDesignOptions }, 'showDesignOptions', !form.showDesignOptions)" v-text="p.name"
        />
        <div v-else-if="p.type === 'addDiscussion' && canEdit" v-tooltip="p.name" class="w-6 h-6 m-1.5 cursor-pointer">
          <font-awesome-icon class="w-6 h-6" icon="fa-light fa-comment" @click="onAddDiscussion" />
        </div>
        <div v-else-if="p.type === 'hideInactive'" v-tooltip="{ placement: 'top', text: form.hideInactive ? t('whiteboard.dynamic.showInactive') : t('whiteboard.dynamic.hideInactive') }" class="w-6 h-6 m-1.5 cursor-pointer">
          <tx-toggle-icon :model-value="form.hideInactive" off-state-icon="fa-light fa-eye" on-state-icon="fa-light fa-eye-slash" class="w-6 h-6" @toggle="onHideInactivePropChange" />
        </div>
        <div v-else-if="p.type === 'info' && canEdit" v-tooltip="p.name" class="w-6 h-6 m-1.5 cursor-pointer">
          <font-awesome-icon class="w-6 h-6" icon="fa-light fa-info-circle" @click="onOpenSelection($event, p.type)" />
        </div>
        <div
          v-else-if="p.type === 'icons'" v-tooltip="{ placement: 'top', text: p.name }" class="w-6 h-6 m-1.5 cursor-pointer"
          @click="onOpenSelection($event, p.type)"
        >
          <font-awesome-icon class="w-6 h-6" icon="fa-light fa-icons" />
        </div>
      </div>

      <!-- MORE OPTIONS -->
      <tx-button
        v-show="!isEmpty(selectedObjectSubMenu) && canEdit" v-tooltip="{ placement: 'top', text: t('general.more').toLowerCase() }" type="icon" faicon="fa-light fa-ellipsis-stroke-vertical"
        class="float-right" @click="onOpenSelection($event as MouseEvent, 'subMenu')"
      />
    </div>

    <!-- DROPDOWNS -->
    <div
      v-show="dropdownVisible === 'border'"
      class="absolute z-[1000] bg-white rounded-sm shadow-toolbar w-40 p-2 text-center"
      :style="{ left: `${propDropdownPos.x}px`, top: `${propDropdownPos.y}px` }"
    >
      <input
        v-model="form.borderThickness" type="range" class="w-100" min="1" max="50"
        @change="onInputChange('border', { borderColor: form.borderColor, strokeWidth: form.borderThickness }, 'borderColor', form.borderColor)"
      >
      <div
        v-for="color in availableColors" :key="color" class="rounded-full w-6 h-6 float-left m-1.5 cursor-pointer border"
        :style="{ backgroundColor: color, borderColor: utils.darkenColor(color, 0.3) }" :class="{ 'outline outline-blue-50 outline-2 outline-offset-1': form.borderColor === color }"
        @click="onInputChange('border', { borderColor: color, strokeWidth: form.borderThickness }, 'borderColor', color)"
      >
        <div v-if=" color === 'transparent'" class="absolute rotate-45 top-[47px] w-[23px] h-[1px] bg-gray-400" />
      </div>
    </div>

    <div
      v-show="dropdownVisible === 'color'" class="absolute z-[1000] bg-white rounded-sm shadow-toolbar w-40 p-2"
      :style="{ left: `${propDropdownPos.x}px`, top: `${propDropdownPos.y}px` }"
    >
      <div
        v-for="color in availableColors" :key="color" class="rounded-full w-6 h-6 float-left m-1.5 cursor-pointer border"
        :style="{ backgroundColor: color, borderColor: utils.darkenColor(color, 0.3) }" :class="{ 'outline outline-blue-50 outline-2 outline-offset-1': form.color === color }"
        @click="onInputChange('color', { backgroundColor: color }, 'color', color)"
      >
        <div v-if=" color === 'transparent'" class="absolute rotate-45 top-[26px] w-[23px] h-[1px] bg-gray-400" />
      </div>
    </div>

    <div
      v-show="dropdownVisible === 'backgroundColor'" class="absolute z-[1000] bg-white rounded-sm shadow-toolbar w-40 p-2"
      :style="{ left: `${propDropdownPos.x}px`, top: `${propDropdownPos.y}px` }"
    >
      <div
        v-for="color in availableColors" :key="color" class="rounded-full w-6 h-6 float-left m-1.5 cursor-pointer border"
        :style="{ backgroundColor: color, borderColor: utils.darkenColor(color, 0.3) }" :class="{ 'outline outline-blue-50 outline-2 outline-offset-1': form.backgroundColor === color }"
        @click="onInputChange('backgroundColor', { backgroundColor: color }, 'backgroundColor', color)"
      >
        <div v-if=" color === 'transparent'" class="absolute rotate-45 top-[26px] w-[23px] h-[1px] bg-gray-400" />
      </div>
    </div>

    <div
      v-show="dropdownVisible === 'font'" class="absolute z-[1000] bg-white rounded-sm shadow-toolbar min-w-60 max-w-80 p-2 max-h-60 overflow-y-auto"
      :style="{ left: `${propDropdownPos.x}px`, top: `${propDropdownPos.y}px` }"
    >
      <div
        v-for="font in availableFonts" :key="font" class="text-sm h-6 my-auto m-1.5 cursor-pointer whitespace-nowrap"
        :class="{ 'font-bold': form.font === font }" @click="onInputChange('font', { font }, 'font', font)" v-text="font"
      />
    </div>

    <div
      v-show="dropdownVisible === 'fontSize'"
      class="absolute z-[1000] bg-white rounded-sm shadow-toolbar w-20 p-2"
      :style="{ left: `${propDropdownPos.x}px`, top: `${propDropdownPos.y}px` }"
    >
      <input v-model="form.fontSize" type="number" min="1" max="100" @change="onInputChange('fontSize', { fontSize: form.fontSize }, 'fontSize', form.fontSize)">
      <div
        v-for="(val, key) in availableFontSizes" :key="key" class="text-sm h-6 my-auto m-1.5 cursor-pointer"
        :class="{ 'font-bold': form.fontSize === key }" @click="onInputChange('fontSize', { fontSize: key }, 'fontSize', key)" v-text="val"
      />
    </div>

    <div
      v-show="dropdownVisible === 'textColor'"
      class="absolute z-[1000] bg-white rounded-sm shadow-toolbar w-40 p-2"
      :style="{ left: `${propDropdownPos.x}px`, top: `${propDropdownPos.y}px` }"
    >
      <div
        v-for="textColor in availableColors" :key="textColor" class="rounded-full w-6 h-6 float-left m-1.5 cursor-pointer border"
        :style="{ backgroundColor: textColor, borderColor: utils.darkenColor(textColor, 0.3) }"
        @click="onInputChange('textColor', { textColor }, 'textColor', textColor)"
      >
        <div
          v-if=" textColor === 'transparent'" class="absolute rotate-45 top-[26px] w-[23px] h-[1px] bg-gray-400"
        />
      </div>
    </div>

    <div
      v-show="dropdownVisible === 'textAlign'"
      class="absolute z-[1000] bg-white rounded-sm shadow-toolbar w-30 p-2"
      :style="{ left: `${propDropdownPos.x - 50}px`, top: `${propDropdownPos.y}px` }"
    >
      <font-awesome-icon
        v-for="(icon, alignType) in availableTextAligns" :key="alignType" class="rounded-full w-6 h-6 float-left m-1.5 cursor-pointer"
        :icon="icon" @click="onInputChange('textAlign', { textAlign: alignType }, 'textAlign', alignType)"
      />
    </div>

    <div
      v-show="dropdownVisible === 'fontStyle'" class="absolute z-[1000] bg-white rounded-sm shadow-toolbar w-30 p-2"
      :style="{ left: `${propDropdownPos.x}px`, top: `${propDropdownPos.y}px` }"
    >
      <div
        v-tooltip="t('general.bold')" class="float-left m-1.5 text-base cursor-pointer" :class="{ 'text-primary-500': form.bold }"
        @click="onInputChange('bold', { bold: !form.bold }, 'bold', !form.bold)" v-text="'B'"
      />
      <div
        v-tooltip="t('general.italic')" class="float-left m-1.5 text-base italic cursor-pointer" :class="{ 'text-primary-500': form.italic }"
        @click="onInputChange('italic', { italic: !form.italic }, 'italic', !form.italic)" v-text="'I'"
      />
      <div
        v-tooltip="t('general.underline')" class="float-left m-1.5 text-base cursor-pointer border-b-2 border-black" :class="{ 'text-primary-500': form.underline }"
        @click="onInputChange('underline', { underline: !form.underline }, 'underline', !form.underline)" v-text="'U'"
      />
    </div>

    <div
      v-show="dropdownVisible === 'size'" class="absolute z-[1000] bg-white rounded-sm shadow-toolbar w-8 p-2"
      :style="{ left: `${propDropdownPos.x}px`, top: `${propDropdownPos.y}px` }"
    >
      <div
        v-for="size in availableSizes" :key="size" class="text-sm h-6 my-auto m-1.5 cursor-pointer"
        :class="{ 'font-bold': form.size === size }" @click="onInputChange('size', { size }, 'size', size)" v-text="size"
      />
    </div>

    <div
      v-show="dropdownVisible === 'shapeType'"
      class="absolute z-[1000] bg-white rounded-sm shadow-toolbar w-40 p-2"
      :style="{ left: `${propDropdownPos.x}px`, top: `${propDropdownPos.y}px` }"
    >
      <div
        v-for="(path, shapeType) in availableShapes" :key="shapeType" class="rounded-full w-6 h-6 float-left m-1.5 cursor-pointer"
        @click="onInputChange('shapeType', { shapeType }, 'shapeType', shapeType)"
      >
        <svg viewBox="0 0 50 50">
          <path :style="{ fill: 'none', stroke: form.shapeType === shapeType ? '#1484f8' : '#000000', strokeWidth: '3px' }" :d="path" />
        </svg>
      </div>
    </div>

    <div
      v-show="dropdownVisible === 'frameSize'" class="absolute z-[1000] bg-white rounded-sm shadow-toolbar w-42 p-2"
      :style="{ left: `${propDropdownPos.x}px`, top: `${propDropdownPos.y}px` }"
    >
      <div
        v-for="(path, frameSize) in availableFrameSizes" :key="frameSize" class="rounded-full float-left m-1.5 cursor-pointer"
        @click="onInputChange('frameSize', { frameSize }, 'frameSize', frameSize)"
      >
        <div class="w-6 h-6">
          <svg viewBox="0 0 24 24">
            <path :style="{ fill: 'none', stroke: form.frameSize === frameSize ? '#1484f8' : '#000000' }" :d="path" />
          </svg>
        </div>
        <div class="text-center">
          {{ utils.capitalizeFirstLetter(frameSize) }}
        </div>
      </div>
    </div>

    <div
      v-show="dropdownVisible === 'imageType'"
      class="absolute z-[1000] bg-white rounded-sm shadow-toolbar w-40 p-2"
      :style="{ left: `${propDropdownPos.x}px`, top: `${propDropdownPos.y}px` }"
    >
      <div
        v-for="asset in imageTypeOptions" :key="asset.Key" class="text-sm h-6 my-auto m-1.5 cursor-pointer"
        :class="{ 'font-bold': form.assetKey === asset.Key }" @click="onAssetPropChange(asset)" v-text="asset.Value"
      />
    </div>

    <div
      v-show="dropdownVisible === 'scale'" class="absolute z-[1000] bg-white rounded-sm shadow-toolbar w-20 p-2"
      :style="{ left: `${propDropdownPos.x}px`, top: `${propDropdownPos.y}px` }"
    >
      <input v-model="form.scale" type="number" min="1" max="100" @change="onInputChange('scale', { scale: form.scale }, 'scale', form.scale)">
      <div
        v-for="scale in availableScales" :key="scale" class="text-sm h-6 my-auto m-1.5 cursor-pointer"
        :class="{ 'font-bold': form.scale === scale }" @click="onInputChange('scale', { scale }, 'scale', scale)" v-text="`${scale}%`"
      />
    </div>

    <div
      v-show="dropdownVisible === 'icons'"
      class="absolute z-[1000] bg-white rounded shadow-toolbar w-44 p-3"
      :style="{ left: `${propDropdownPos.x}px`, top: `${propDropdownPos.y}px` }"
    >
      <div class="grid grid-cols-3 gap-3">
        <div
          v-for="icon in whiteboardConstants.availableIcons"
          :key="icon.name"
          class="w-12 h-12 flex items-center justify-center rounded cursor-pointer transition-all border-2 text-3xl"
          :class="{
            'bg-blue-100 border-blue-500 scale-110': form.icons.includes(icon.name),
            'border-gray-200 hover:bg-gray-50 hover:scale-105': !form.icons.includes(icon.name),
            'opacity-50 cursor-not-allowed hover:scale-100 hover:bg-transparent':
              form.icons.length >= 3 && !form.icons.includes(icon.name),
          }"
          @click="form.icons.length < 3 || form.icons.includes(icon.name) ? onIconToggle(icon.name) : null"
        >
          {{ icon.icon }}
        </div>
      </div>

      <div class="mt-3 pt-3 border-t border-gray-200 flex justify-between items-center">
        <span class="text-xs text-gray-500">
          {{ form.icons.length }}/3
        </span>
        <button type="button" class="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200" @click="onClearIcons">
          Clear
        </button>
      </div>
    </div>

    <div
      v-show="dropdownVisible === 'info'" class="absolute z-[1000] bg-white rounded-sm shadow-toolbar w-48 p-2"
      :style="{ left: `${propDropdownPos.x}px`, top: `${propDropdownPos.y}px` }"
    >
      <div class="space-y-4 p-1 text-sm text-gray-700">
        <div class="flex flex-col">
          <span class="text-gray-500">{{ t('general.createdBy') }}:</span>
          <template v-if="infoList.length > 0">
            <span class="font-medium text-gray-800">{{ infoList[0].userId }}</span>
            <span v-tooltip="formatDateTime(infoList[0].timestamp)" class="text-gray-600">
              <use-time-ago v-slot="{ timeAgo }" :time="new Date(infoList[0].timestamp)">
                {{ timeAgo }}
              </use-time-ago>
            </span>
          </template>
          <div v-else class="skeleton">
            <title-skeleton />
          </div>
        </div>
        <div class="flex flex-col">
          <span class="text-gray-500">{{ t('general.lastModifiedBy') }}:</span>
          <template v-if="infoList.length > 1">
            <span class="font-medium text-gray-800">{{ infoList[1].userId }}</span>
            <span v-tooltip="formatDateTime(infoList[1].timestamp)" class="text-gray-600">
              <use-time-ago v-slot="{ timeAgo }" :time="new Date(infoList[1].timestamp)">
                {{ timeAgo }}
              </use-time-ago>
            </span>
          </template>
          <div v-else class="skeleton">
            <title-skeleton />
          </div>
        </div>
      </div>
    </div>

    <div
      v-show="selectedObj?.type === whiteboardConstants.objectTypes.discussion" id="manageDiscussion"
      class="absolute z-[1000] bg-white rounded-sm shadow-toolbar w-60 p-2" :class="{ 'cursor-not-allowed': isReadOnly }"
      :style="{ left: `${propDropdownPos.x}px`, top: `${propDropdownPos.y}px` }"
    >
      <manage-discussion
        v-if="selectedObj?.type === whiteboardConstants.objectTypes.discussion" :permission="permission"
        :discussion="selectedObj" :whiteboard-id="whiteboardId" :whiteboard="whiteboard" :is-locked="getDiscussionLockProp" @delete="onDeleteDiscussion"
      />
    </div>

    <div
      v-show="dropdownVisible === 'subMenu' && canEdit" class="absolute z-[1000] bg-white rounded-sm shadow-toolbar w-60 p-2"
      :style="{ left: `${propDropdownPos.x}px`, top: `${propDropdownPos.y}px` }"
    >
      <div
        v-for="subMenu in selectedObjectSubMenu"
        :key="subMenu.action" class="flex items-center px-1 text-base leading-9 truncate transition rounded-sm cursor-pointer hover:bg-gray-200 motion-reduce:transition-none motion-reduce:hover:transform-none"
        @click="onSelectedObjectSubMenuClick(subMenu.action)"
      >
        <!-- <tx-button type="icon" :icon="subMenu.icon" :faicon="subMenu.faicon" /> -->
        <font-awesome-icon :icon="subMenu.faicon" class="w-4 h-4" />
        <p class="ml-1">
          {{ subMenu.label }}
        </p>
      </div>
    </div>

    <input
      v-show="false" ref="assignImageRef" type="file" accept="image/png, image/jpg, image/jpeg"
      @change="onAssignImageChange"
    >

    <assign-image-dialog
      ref="assignImageDialogRef" :allow-image="allowImageToArticle" :allow-resource="allowResourceToArticle"
      @cancel="resetAssignImage" @confirm="onAssignImageConfirm" @confirm-design-option-image-as-article-image="assignDesignOptionAsArticleImage"
    />

    <dynamic-grid-properties ref="dynamicPropertiesRef" @confirm="onAssignDynamicProperties" />
    <!-- Attributes Chooser -->
    <tx-select-chooser
      ref="attributeChooserRef"
      v-model:selected-items="form.attributes"
      :items="availableAttributes" item-key-prop="SystemName" item-display-prop="DisplayName" @update:selected-items="onAttributePropChange"
    />
    <!-- Thumbnail Attributes Chooser -->
    <tx-select-chooser
      ref="thumbnailAttributeChooserRef"
      v-model:selected-items="form.thumbnailAttributes"
      :items="availableAttributes" item-key-prop="SystemName" item-display-prop="DisplayName" @update:selected-items="onThumbnailAttributePropChange"
    />
  </div>
</template>

<script setup lang="ts">
import { clone, isEmpty } from 'lodash-es'
import { fabric } from 'fabric'
import { useI18n } from 'vue-i18n'
import { computedAsync, useElementSize, useWindowSize } from '@vueuse/core'
import { computed, nextTick, onMounted, onUnmounted, reactive, ref } from 'vue'
import type { ActiveSelection } from 'fabric/fabric-impl'
import { UseTimeAgo } from '@vueuse/components'
import WbArticleDetails from '../services/articleDetails'
import type Whiteboard from '../services/whiteboard'
import WbShape from '../services/shape'
import WbFrame from '../services/frame'
import WbImage from '../services/image'
import WbDiscussionIcon from '../services/discussionIcon'
import type WbArticleImage from '../services/articleImage'
import WbModelDetails from '../services/modelDetails'
import type WbModelImage from '../services/modelImage'
import Utility from '../services/utility'
import WbDynamic from '../services/dynamic'
import type { DynamicTemplateOptionValue } from '../services/templates/dynamicGrid/dynamicGridTemplateFactory'
import WbDesignOption from '../services/designOption'
import WbDunesImage from '../services/dunesImage'
import type WbTextBox from '../services/textBox'
import AssignImageDialog from './AssignImageDialog.vue'
import DynamicGridProperties from './DynamicGridProperties.vue'
import ManageDiscussion from './ManageDiscussion.vue'
import type DuneAsset from '@/models/duneAsset'
import type MyArticle from '@/models/myArticle'
import type { CreateUpdateAssetModel } from '@/api/dune/model/assetModel'
import { getArticleAssets } from '@/api/t1/article'
import { appConstants, imageConstants, privileges, whiteboardConstants } from '@/models/constants'
import utils, { formatDateTime } from '@/services/utils'
import TxButton from '@/shared/components/TxButton.vue'
import TxToggleIcon from '@/shared/components/TxToggleIcon.vue'
import { useNotificationStore } from '@/store/notification'
import { useUserStore } from '@/store/userData'
import { useWhiteboardStore } from '@/store/whiteboard'
import type { FilterCriteria } from '@/models/filterCriteria'
import TxSelectChooser from '@/shared/components/TxSelectChooser.vue'
import TitleSkeleton from '@/shared/components/TitleSkeleton.vue'
import { getBoardObjectInfo } from '@/api/whiteboard/boards'
import type { BoardObjectInfo } from '@/api/whiteboard/model/boardModel'
import appConfig from '@/services/appConfig'
import useEventsBus from '@/shared/composables/eventBus'
import type Article from '@/models/article'
import { getAssetById } from '@/api/dune/asset'
import { AttributeType } from '@/models/catalogAttribute'

const props = defineProps<{
  whiteboardId: number
  whiteboard: Whiteboard
  permission: WhiteboardUserPermissionType
}>()

const emit = defineEmits<{
  (e: 'openTextEditingDialog', object: IWbObject)
  (e: 'copyModel', activeObject: WbArticleImage | WbModelImage)
  (e: 'copyArticles', activeObjects: WbArticleImage)
  (e: 'editArticles', activeObjects: WbArticleImage[])
  (e: 'saveImages', activeObjects: WbArticleImage[], isWhiteboard: boolean)
  (e: 'editFavorites', objects: WbArticleImage[])
  (e: 'handleImagesDrop', files: File[], currentPointer: { x: number, y: number })
  (e: 'deleteImage', activeObject: WbArticleImage)
  (e: 'designOptionAdded', assetId: string)
  (e: 'articleUpdated', articles: MyArticle[])
}>()

const { t } = useI18n()
const userStore = useUserStore()
const notificationsStore = useNotificationStore()
const { onAppEvent, offAppEvent } = useEventsBus()
const whiteboardStore = useWhiteboardStore()

const toolbarVisible = ref(false)
const editableProps = ref<Record<string, IWbObjectProp>>({})
const selectedObjectSubMenu = ref<Record<string, IWObjectActions>>({})
const winSize = useWindowSize()
const toolbarRef = ref<HTMLElement>()
const assignImageRef = ref<HTMLInputElement>()
const assignImageDialogRef = ref<InstanceType<typeof AssignImageDialog>>()
const dynamicPropertiesRef = ref<InstanceType<typeof DynamicGridProperties>>()
const toolbarSize = useElementSize(toolbarRef)
const selectedObj = ref()
const doesSelectionContainsModelDetails = ref(false) // TODO: this flag is not required if selectedObj hold all the selected objects (probably logic on selectedObj should be revisited)
const doesSelectionContainsArticleDetails = ref(false) // TODO: this flag is not required if selectedObj hold all the selected objects (probably logic on selectedObj should be revisited)
const selObjCoords = ref<[fabric.Point, fabric.Point, fabric.Point, fabric.Point]>()
const availableColors = ['transparent', '#fff9b1', '#d5f692', '#f5d128', '#c9df56', '#ff9d48', '#93d275', '#f16c7f', '#67c6c0', '#ea94bb', '#6cd8fa', '#ffcee0', '#a6ccf5', '#be88c7', '#7b92ff', '#000000', '#D3D3D3', '#B8B8B8', '#808080', '#FFFFFF']
const availableFontSizes = { '-1': 'Auto', '4': '4', '8': '8', '10': '10', '12': '12', '14': '14', '18': '18', '20': '20', '24': '24', '32': '32', '48': '48', '64': '64' }
const availableTextAligns = { left: 'fa-light fa-align-left', center: 'fa-light fa-align-center', right: 'fa-light fa-align-right' }
const availableSizes = ['S', 'M', 'L']
const availableShapes = WbShape.shapes
const availableFrameSizes = (({ ledger, ...o }) => o)(WbFrame.frameSizes)
const availableAssets = ref<DuneAsset[]>([])
const availableScales = [100, 50, 25, 10, 5]
const dropdownVisible = ref('')
const propDropdownPos = ref<IPoint>({ x: 0, y: 0 })
const form = reactive({
  borderThickness: 1,
  borderColor: '#000000',
  color: '#93d275',
  backgroundColor: 'transparent',
  font: 'Helvetica',
  fontSize: '-1',
  textColor: '#000000',
  textAlign: 'center',
  bold: false,
  italic: false,
  underline: false,
  size: 'M',
  shapeType: 'rectangle',
  frameSize: 'custom',
  lock: false,
  preventUnlock: false,
  assetKey: '',
  scale: 100,
  showLabels: true,
  showResources: false,
  showDesignOptions: false,
  attributes: [],
  thumbnailAttributes: [],
  hideInactive: false,
  discussionLock: false, // to enable/disable the comments actions when user lock unlock
  icons: [] as string[],
})
const panning = ref(false)
const attributeChooserRef = ref<InstanceType<typeof TxSelectChooser>>()
const thumbnailAttributeChooserRef = ref<InstanceType<typeof TxSelectChooser>>()
const showDeleteImage = ref(false)
let articleImageObjectForImageAssign: WbArticleImage

let currentDynamicObjs: WbDynamic[] | undefined
let currentObjPos: { x: number, y: number } | undefined

let selectedArticleImage: WbArticleImage | undefined

const isReadOnly = computed(() => props.permission === whiteboardConstants.userPermissions.read)
const canEdit = computed(() => props.permission === whiteboardConstants.userPermissions.write)

const allowImageToArticle = computed(() => (userStore.userProfile.isValidPrivilege(privileges.whiteboard.allowExternalImageToArticle) && utils.isValidStringValue(userStore.activeCatalog?.ContextReadWriteKey)))

const allowResourceToArticle = computed(() => (userStore.userProfile.isValidPrivilege(privileges.resource.create) && userStore.userProfile.isValidPrivilege(privileges.resource.assign)))

const allowExternalImageToArticle = computed(() => (canEdit.value && (allowImageToArticle.value || allowResourceToArticle.value)))

const getDiscussionLockProp = computed(() => form.discussionLock || selectedObj.value.lock)

const availableFonts = computed(() => {
  const customFonts = utils.isDefined(appConfig.dynamicConfig) && utils.isDefined(appConfig.dynamicConfig.customFonts) && appConfig.dynamicConfig.customFonts.length
    ? appConfig.dynamicConfig.customFonts
    : []

  return [...new Set([...customFonts, ...whiteboardConstants.defaultFontFamily])].sort()
})

const allowDeleteImage = computed(() => showDeleteImage.value)

const imageTypeOptions = computed(() => {
  if (availableAssets.value.length) {
    const assetOptions = availableAssets.value.map(asset => ({
      Key: asset.Id,
      Value: asset.Key,
    }))
    assetOptions.push(
      { Key: 'newestImage', Value: 'Newest Image' },
      { Key: 'firstImage', Value: 'First Image' },
    )
    return assetOptions
  }
  return []
})

const getImageTypeText = computed(() => {
  const imageType = imageTypeOptions.value.find(option => option.Key === form.assetKey)
  return imageType ? imageType.Value : 'Type'
})

const availableAttributes = computed(() => {
  const excludeAttributeSystemName = new Set(['_DeliveryDates', '_Segmentations', '_FavoriteTags'])

  const availableAttributes = Object.values(userStore.myAttributes!).filter(attribute => !excludeAttributeSystemName.has(attribute.SystemName) && (!doesSelectionContainsModelDetails.value || attribute.IsModelLevel) && attribute.AttributeType !== AttributeType.Dataset)
  // selection does not contains article details, add SKU fields
  if (doesSelectionContainsModelDetails.value && !doesSelectionContainsArticleDetails.value) {
    const SKUField = { ...appConstants.staticFieldTemplate }
    SKUField.DisplayName = `${userStore.activeCatalog!.Season} SKU`
    SKUField.SystemName = userStore.activeCatalog!.CatalogCode.toString()
    availableAttributes.push(SKUField)
    for (const key in userStore.linkedCatalogDetails) {
      const SKUField = { ...appConstants.staticFieldTemplate }
      SKUField.DisplayName = `${userStore.linkedCatalogDetails[key].Season} SKU`
      SKUField.SystemName = userStore.linkedCatalogDetails[key].CatalogCode.toString()
      availableAttributes.push(SKUField)
    }
  }
  return availableAttributes
})

const infoList = computedAsync(
  async () => {
    const result: BoardObjectInfo[] = []
    if (dropdownVisible.value === 'info' && selectedObj.value && selectedObj.value.id) {
      try {
        const resp = await getBoardObjectInfo(props.whiteboard.boardId, selectedObj.value.id)
        if (resp && resp.data) {
          if (resp.data.length > 0) {
            result.push(resp.data[0])
            result.push(resp.data[resp.data.length - 1])
          }
        }
      }
      catch (e) {
        console.error(e)
      }
    }
    return result
  },
  [],
)

// watchEffect(() => {
//   if (props.whiteboard) {
//     attach(props.whiteboard)
//   }
// })
onMounted(() => {
  if (props.whiteboard) {
    attach(props.whiteboard)
    onAppEvent('reloadDeletedArticleImage', reloadDeletedArticleImage)
  }
})
onUnmounted(() => {
  if (props.whiteboard) {
    detach(props.whiteboard)
    offAppEvent('reloadDeletedArticleImage', reloadDeletedArticleImage)
  }
})

function attach(obj: Whiteboard) {
  obj.on('pan', handlePanning)

  obj.canvas.on('selection:created', onSelection)
  obj.canvas.on('selection:updated', onSelection)
  obj.canvas.on('selection:cleared', onSelection)
  obj.canvas.on('object:moving', (e) => {
    onSelection(e)
    resetSelectedArticleImage()
    // if moving object is an image and allowExternalImageToArticle is true, then highligh the article image if image intersects it and initialize selectedArticleImage (assign this image to the intersected article)
    if ((e.target?.type === whiteboardConstants.objectTypes.image || e.target?.type === whiteboardConstants.objectTypes.dunesImage || e.target?.type === whiteboardConstants.objectTypes.designOption) && e.pointer && allowExternalImageToArticle.value) {
      const articleImages = obj.canvas.getObjects(whiteboardConstants.objectTypes.articleImage)
      for (let i = 0; i < articleImages.length; i++) {
        const articleImage = articleImages[i] as WbArticleImage
        // if image intersect with an article image and selectedArticleImage is not yet assigned, then highlight the article image and assign it to selectedArticleImage
        if (!selectedArticleImage && articleImage.midIntersectsWithObject(e.target)) {
          articleImage.highlight()
          selectedArticleImage = articleImage
        }
        else {
          articleImage.dim()
        }
      }
    }
  })
  obj.canvas.on('object:scaling', onSelection)
  obj.on('zoom', onSelection)
  obj.canvas.on('mouse:up', (e) => {
    if (panning.value) {
      panning.value = false
    }

    if (e.target?.hasOwnProperty('_objects')) {
      const selectedObjects = (e.target as fabric.ActiveSelection).getObjects() as Array<IWbObject>
      const selectedObjectIds = new Set(selectedObjects.filter(x => x.id).map(x => x.id!))
      obj.canvas.discardActiveObject()
      selectedObjects.forEach((object) => {
        utils.addOrRemoveChildToFrames(obj.frames.value, object, selectedObjectIds)
      })
      const selection = new fabric.ActiveSelection(selectedObjects, { canvas: obj.canvas })
      obj.canvas.setActiveObject(selection)
      obj.canvas.requestRenderAll()
    }
    else {
      utils.addOrRemoveChildToFrames(obj.frames.value, e.target as IWbObject)
    }
    if (userStore.activeCatalog && selectedArticleImage && selectedArticleImage.highlighted) {
      const activeObj = props.whiteboard.canvas.getActiveObject()
      if (activeObj instanceof WbImage) {
        const url = activeObj.getSrc()
        fetch(url)
          .then(response => response.blob())
          .then((blob) => {
            const filename = url.split('/').pop()
            if (filename) {
              const file = new File([blob], filename)
              // assign image to article image (drag an image over an article image on whiteboard)
              openAssignImageDialog(selectedArticleImage!, file)
            }
          })
      }
      else if (activeObj instanceof WbDunesImage) {
        if (utils.isValidStringValue(activeObj.assetId)) {
          getAssetById(userStore.activeCatalog.ContextKey, activeObj.assetId).then((resp) => {
            openAssignImageDialog(selectedArticleImage!, undefined, resp.data)
          })
        }
        else {
          const url = activeObj.getSrc()
          fetch(url)
            .then(response => response.blob())
            .then((blob) => {
              const params = new URL(url).searchParams
              const filename = params.get('ImageSet')
              if (filename) {
                const file = new File([blob], `${filename}.webp`, { type: 'webp' })
                // assign image to article image (drag an image over an article image on whiteboard)
                openAssignImageDialog(selectedArticleImage!, file)
              }
            })
        }
      }
      else if (activeObj instanceof WbDesignOption) {
        articleImageObjectForImageAssign = selectedArticleImage
        assignDesignImageAsArticleImage(activeObj)
      }
    }
  })
  obj.canvas.on('dragover', (e) => {
    if (!canEdit.value) { return }

    resetSelectedArticleImage()
    if (allowExternalImageToArticle.value) {
      const articleImages = obj.canvas.getObjects(whiteboardConstants.objectTypes.articleImage)
      for (let i = 0; i < articleImages.length; i++) {
        const articleImage = articleImages[i] as WbArticleImage
        if (e.target === articleImage) {
          articleImage.highlight()
        }
        else {
          articleImage.dim()
        }
      }
    }
  })
  obj.canvas.on('drop', (e) => {
    if (!canEdit.value) { return }

    e.e.preventDefault()
    const dragEvent = e.e as DragEvent
    if (e.target?.type === whiteboardConstants.objectTypes.articleImage && allowExternalImageToArticle.value && dragEvent.dataTransfer?.files.length) {
      let selectedFile: File | undefined
      const articleImage = e.target as WbArticleImage
      for (let i = 0; i < dragEvent.dataTransfer.files.length; i++) {
        const image = dragEvent.dataTransfer.files[i]
        if (imageConstants.validImageFormats.has(image.type)) {
          selectedFile = image
          break
        }
      }
      if (selectedFile) {
        selectedArticleImage = articleImage
        // assign an image to selectedArticleImage by dropping an image form user's machine
        openAssignImageDialog(selectedArticleImage, selectedFile)
      }
      else {
        articleImage.dim()
      }
    }
    else if (allowExternalImageToArticle.value && dragEvent.dataTransfer?.files.length) {
      const selectedFiles: File[] = []
      for (let i = 0; i < dragEvent.dataTransfer.files.length; i++) {
        const image = dragEvent.dataTransfer.files[i]
        if (imageConstants.validImageFormats.has(image.type)) {
          selectedFiles.push(image)
        }
      }
      if (selectedFiles.length) {
        emit('handleImagesDrop', selectedFiles, obj.canvas.getPointer(dragEvent))
      }
    }
  })
  obj.canvas.on('object:modified', (e) => {
    // when there are no valid resource images set the show resources false
    if (e.target?.type === whiteboardConstants.objectTypes.articleImage) {
      const articleObject = e.target as WbArticleImage
      form.showResources = articleObject.showResources
      form.showDesignOptions = articleObject.showDesignOptions
    }
    if (e.target?.type === whiteboardConstants.objectTypes.articleImage || e.target?.type === whiteboardConstants.objectTypes.modelImage || e.target?.type === whiteboardConstants.objectTypes.modelDetails) {
      const currentObject = e.target as WbModelImage | WbModelDetails
      form.showDesignOptions = currentObject.showDesignOptions
    }
    else if (e.target?.type === whiteboardConstants.objectTypes.discussion) { // when comment is locked update the manage discussion
      const discussionObject = e.target as WbDiscussionIcon
      form.discussionLock = discussionObject.lock
    }
    else if (e.target?.type === whiteboardConstants.objectTypes.textBox) {
      const textboxObject = e.target as WbTextBox
      if (textboxObject.fontSize) {
        form.fontSize = textboxObject.fontSize.toString()
      }
    }
  })
}

function detach(obj: Whiteboard) {
  obj.canvas.off('selection:created')
  obj.canvas.off('selection:updated')
  obj.canvas.off('selection:cleared')
  obj.canvas.off('object:moving')
  obj.canvas.off('object:scaling')
  obj.canvas.off('mouse:up')
  obj.canvas.off('dragover')
  obj.canvas.off('drop')
  obj.canvas.off('object:modified')
  obj.off('pan', handlePanning)
}

const toolbarPos = computed(() => {
  if (!selObjCoords.value || panning.value) { return { x: 0, y: 0 } }
  const topMargin = 60
  const bottomMargin = 40
  const canvasZoom = props.whiteboard?.canvas.getZoom() || 0
  const vpCoords = props.whiteboard?.canvas.vptCoords
  let x = (selObjCoords.value[0].x - vpCoords!.tl.x)
  let y = (selObjCoords.value[0].y - vpCoords!.tl.y)
  const bottomY = (selObjCoords.value[3].y - vpCoords!.tl.y) * canvasZoom
  const width = selObjCoords.value[1].x - selObjCoords.value[0].x

  // Calculate toolbar position
  x = x * canvasZoom + (width * canvasZoom / 2) - toolbarSize.width.value / 2
  y = (y * canvasZoom - toolbarSize.height.value - topMargin) // - dynamicAdjustment

  const availableSpaceAbove = y
  const availableSpaceBelow = winSize.height.value - bottomY - toolbarSize.height.value - bottomMargin

  if (availableSpaceAbove < 0 && availableSpaceBelow > toolbarSize.height.value) {
    y = bottomY + bottomMargin
  }

  x = Math.min(Math.max(x, 0), winSize.width.value - toolbarSize.width.value - 100)
  y = Math.max(y, bottomMargin)
  return { x, y }
})

function onIconToggle(iconName: string) {
  if (form.icons.length >= 3 && !form.icons.includes(iconName)) {
    return
  }
  const currentIcons = [...form.icons]
  const iconIndex = currentIcons.indexOf(iconName)
  if (iconIndex > -1) {
    currentIcons.splice(iconIndex, 1)
  }
  else {
    currentIcons.push(iconName)
  }
  onInputChange('icons', { icons: currentIcons }, 'icons', currentIcons)
}

function onClearIcons() {
  onInputChange('icons', { icons: [] }, 'icons', [])
}

function onSelection(e) {
  if (selectedObj.value?.type === whiteboardConstants.objectTypes.discussion) {
    const discussionObjects = props.whiteboard.canvas.getObjects(whiteboardConstants.objectTypes.discussion) as Array<IWbObject>
    const activeObj: any = discussionObjects.find(o => o.id === selectedObj.value.id)
    if (activeObj && activeObj.type === whiteboardConstants.objectTypes.discussion && activeObj.localOnly && !activeObj.comments.length) {
      props.whiteboard.removeObjects([activeObj])
    }
  }
  dropdownVisible.value = ''
  selectedObj.value = null
  editableProps.value = {}
  selectedObjectSubMenu.value = {}
  doesSelectionContainsModelDetails.value = false
  doesSelectionContainsArticleDetails.value = false
  const objs = props.whiteboard.canvas.getActiveObjects() as Array<IWbObject>
  const selection = props.whiteboard.canvas.getActiveObject()
  if (!objs || !objs.length || !selection || objs.every(o => !o.id)) {
    toolbarVisible.value = false
    return
  }

  // if (objs.some(itm => !(itm as any).editableProps || itm.type !== objs[0].type)) {
  //   toolbarVisible.value = false
  //   dropdownVisible.value = ''
  //   return
  // }

  // if later decided to show actions that are uncommon between objects then while performing the action, the action should be performed on supported objects (check while performing action)
  let articleImagesObjects = objs.filter(obj => obj.type === whiteboardConstants.objectTypes.articleImage) as WbArticleImage[]
  articleImagesObjects = articleImagesObjects.filter(obj => obj.catalogCode === userStore.activeCatalog?.CatalogCode)
  let commonSubMenu = Object.fromEntries((Object.entries((objs[0] as any).actions || {})).filter((item: Array<any>) => item[1].showInSubMenu)) as Record<string, IWObjectActions>
  if (articleImagesObjects && articleImagesObjects.length !== 0 && userStore.activeCatalog) {
    commonSubMenu = Object.fromEntries((Object.entries((articleImagesObjects[0] as any).actions || {})).filter((item: Array<any>) => item[1].showInSubMenu)) as Record<string, IWObjectActions>
  }
  form.preventUnlock = false
  for (let i = 0; i < objs.length; i++) {
    // create common subMenu
    if (!isEmpty(commonSubMenu) && i !== 0) { // if commonSubMenu is already empty then don't repeat the loop on actions(not required for index 0 as well)
      for (const actionName in commonSubMenu) {
        if ((!objs[i].actions?.hasOwnProperty(actionName) && actionName !== 'editArticles') || actionName === 'copyModel' || actionName === 'copyArticles') { // copy model and copy article should be visible only if 1 supported object is selected
          delete commonSubMenu[actionName]
        }
      }
    }

    if (objs[i].type === whiteboardConstants.objectTypes.modelDetails) {
      doesSelectionContainsModelDetails.value = true
    }
    if (objs[i].type === whiteboardConstants.objectTypes.articleDetails) {
      doesSelectionContainsArticleDetails.value = true
    }

    if (objs[i].hasOwnProperty('preventUnlock') && objs[i].preventUnlock === true) {
      form.preventUnlock = true
    }
  }
  if (objs.length === 1) {
    for (const actionName in commonSubMenu) {
      if (commonSubMenu[actionName].multiple && commonSubMenu[actionName].multiple === true) {
        delete commonSubMenu[actionName]
      }
    }
  }

  selectedObjectSubMenu.value = commonSubMenu // later on can implement actions that should appear on main menu

  selObjCoords.value = selection.getCoords(true)

  selectedObj.value = (objs[0] as any)
  form.lock = false
  if (selectedObj.value.editableProps) {
    editableProps.value = selectedObj.value.editableProps
    if (selectedObj.value.type === whiteboardConstants.objectTypes.modelDetails && !selectedObj.value.allowDesignOption) {
      delete editableProps.value.showDesignOptions
    }

    // Check if there's any property with editable: true (from article image)
    const hasEditableProp = Object.values(editableProps.value).some(prop => prop.editable === true)
    if (hasEditableProp) {
      editableProps.value = Object.fromEntries(
        Object.entries(editableProps.value).filter(([_, prop]) => prop.editable === true && (!selectedObj.value?.lock || prop.editableWhenLocked) && (prop.type !== 'addDiscussion' || objs.length === 1)),
      )
    }

    for (const key in editableProps.value) {
      switch (key) {
        case 'border':{
          const prop = selectedObj.value.getProp(key)
          form.borderThickness = prop.strokeWidth || 1
          form.borderColor = prop.borderColor || '#000000'
          break
        }
        case 'color': {
          const prop = selectedObj.value.getProp(key)
          form.color = prop.backgroundColor || prop.color || '#93d275'
          break
        }
        case 'backgroundColor': {
          const prop = selectedObj.value.getProp(key)
          form.backgroundColor = prop.backgroundColor || 'transparent'
          break
        }
        case 'font': {
          const prop = selectedObj.value.getProp(key)
          form.font = prop.font || 'Helvetica'
          break
        }
        case 'fontSize': {
          const prop = selectedObj.value.getProp(key)
          form.fontSize = prop.fontSize || '-1'
          break
        }
        case 'textColor': {
          const prop = selectedObj.value.getProp(key)
          form.textColor = prop.textColor || '#000000'
          break
        }
        case 'textAlign': {
          const prop = selectedObj.value.getProp(key)
          form.textColor = prop.textAlign || 'center'
          break
        }
        case 'bold': {
          const prop = selectedObj.value.getProp(key)
          form.bold = prop.bold || false
          break
        }
        case 'italic': {
          const prop = selectedObj.value.getProp(key)
          form.italic = prop.italic || false
          break
        }
        case 'underline': {
          const prop = selectedObj.value.getProp(key)
          form.underline = prop.underline || false
          break
        }
        case 'size': {
          const prop = selectedObj.value.getProp(key)
          form.size = prop.size || 'M'
          break
        }
        case 'shapeType': {
          const prop = selectedObj.value.getProp(key)
          form.shapeType = prop.shapeType || 'rectangle'
          break
        }
        case 'frameSize': {
          const prop = selectedObj.value.getProp(key)
          form.frameSize = prop.frameSize || 'custom'
          break
        }
        case 'lock': {
          const prop = selectedObj.value.getProp(key)
          form.lock = prop.lock || false
          break
        }
        case 'imageType': {
          const prop = selectedObj.value.getProp(key)
          form.assetKey = prop.assetKey || ''
          availableAssets.value = selectedObj.value.assets
          break
        }
        case 'scale': {
          const prop = selectedObj.value.getProp(key)
          form.scale = prop.scale || 100
          break
        }
        case 'showLabels': {
          const prop = selectedObj.value.getProp(key)
          form.showLabels = Boolean(prop.showLabels)
          break
        }
        case 'showResources': {
          const prop = selectedObj.value.getProp(key)
          form.showResources = Boolean(prop.showResources)
          break
        }
        case 'showDesignOptions': {
          const prop = selectedObj.value.getProp(key)
          form.showDesignOptions = Boolean(prop.showDesignOptions)
          break
        }
        case 'attributes': {
          const prop = selectedObj.value.getProp(key)
          form.attributes = prop.attributes || []
          break
        }
        case 'addThumbnailAttributes': {
          const prop = selectedObj.value.getProp('attributes')
          form.thumbnailAttributes = prop.attributes || []
          break
        }
        case 'hideInactive': {
          const prop = selectedObj.value.getProp(key)
          form.hideInactive = prop.hideInactive || false
          break
        }
        case 'deleteImage': {
          const obj = props.whiteboard?.canvas.getActiveObject() as WbArticleImage
          if (obj) {
            shouldShowDeleteImage(obj)
          }
          break
        }
        case 'icons': {
          const prop = selectedObj.value.getProp(key)
          form.icons = prop.icons || []
          break
        }
        default:
          break
      }
    }
    toolbarVisible.value = true
  }
  if (e.e) {
    propDropdownPos.value = { x: e.e.x - 40, y: e.e.y - 40 }
  }
  else if (e.selected && e.selected.length === 1 && e.selected[0].oCoords && e.selected[0].oCoords.tl && e.selected[0].oCoords.tl.x && e.selected[0].oCoords.tl.y) {
    propDropdownPos.value = { x: e.selected[0].oCoords.tl.x, y: e.selected[0].oCoords.tl.y }
  }
}

function onOpenSelection(e: PointerEvent | MouseEvent, type: WbObjectPropType) {
  dropdownVisible.value = dropdownVisible.value === type ? '' : type
  const dropdownHeight = dropdownVisible.value === 'subMenu' ? 300 : 150
  const dropdownWidth = dropdownVisible.value === 'subMenu' ? 240 : 160
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  let x = e.x - 74 // default
  let y = toolbarPos.value.y + toolbarSize.height.value + 10
  // Check if dropdown will go out of bounds
  if (x + dropdownWidth > viewportWidth) { // show it to the left
    x = viewportWidth - dropdownWidth - 10
  }
  if (y + dropdownHeight > viewportHeight) { // show it on top
    y = toolbarPos.value.y - dropdownHeight - 10
  }
  x = Math.max(10, x)
  y = Math.max(10, y)
  propDropdownPos.value = { x, y }
}

function onOpenAttributesSelector() {
  attributeChooserRef.value?.openDialog()
}

function onOpenThumbnailAttributesSelector() {
  thumbnailAttributeChooserRef.value?.openDialog()
}

function onSelectedObjectSubMenuClick(action: string) {
  const activeObject = props.whiteboard.canvas.getActiveObject()
  const activeObjects = props.whiteboard.canvas.getActiveObjects()
  const articleImageObjects = activeObjects.filter(obj => obj.type === whiteboardConstants.objectTypes.articleImage) as WbArticleImage[]
  if (activeObject === null) { return }

  const objectsKey = '_objects'
  let objects
  if (activeObject.type === 'activeSelection') {
    objects = activeObject[objectsKey]
  }
  else {
    objects = [activeObject]
  }
  if (action !== 'cloneAsImage') {
    props.whiteboard.canvas.discardActiveObject()
  }
  switch (action) {
    case 'rename':
      emit('openTextEditingDialog', activeObject as IWbObject)
      break
    case 'delete':
      props.whiteboard.removeObjects(objects, true)
      break
    case 'selectSimilar': {
      const matchingObjects = Utility.getSimilarObjects(props.whiteboard!.canvas, objects)
      if (matchingObjects.length > 0 && props.whiteboard) {
        const selection = new fabric.ActiveSelection(matchingObjects, { canvas: props.whiteboard.canvas })
        props.whiteboard.canvas.setActiveObject(selection)
        props.whiteboard.canvas.requestRenderAll()
      }
      break
    }
    case 'bringFront':
      props.whiteboard.bringObjectsToFront(objects)
      break
    case 'sendBack':
      props.whiteboard.sendObjectsToBack(objects)
      break
    case 'copyLink': {
      const wbObject = activeObject as IWbObject
      if (wbObject.id) {
        const urlParams = new URLSearchParams(window.location.search)
        urlParams.set('moveToObject', wbObject.id)
        const link = `${window.location.origin}${window.location.pathname}?${urlParams.toString()}`
        navigator.clipboard.writeText(link)
          .then(() => notificationsStore.addNotification({ message: t('notifications.linkCopied'), type: 'Success' }))
      }
      break
    }
    case 'copyModel':
      emit('copyModel', activeObject as WbArticleImage | WbModelImage)
      break
    case 'editArticles':
      emit('editArticles', articleImageObjects.filter(obj => obj.catalogCode === userStore.activeCatalog?.CatalogCode) as WbArticleImage [])
      break
    case 'unGroup':
      props.whiteboard.ungroupObject(activeObject, true)
      break
    case 'group':
      props.whiteboard.groupObjects(objects, {}, true)
      break
    case 'editDynamicObjectSettings':
      openDynamicPropertiesDialog(objects)
      break
    case 'copyArticles':
      emit('copyArticles', activeObject as WbArticleImage)
      break
    case 'cloneAsImage':
      copySelectedToClipboard()
      break
    case 'cropImage':
      (activeObject as WbImage).enableCropping()
      break
    case 'saveImages':
      emit('saveImages', articleImageObjects as WbArticleImage [], true)
      break
    case 'editFavorites':
      emit('editFavorites', objects as WbArticleImage [])
      break
    case 'unlink':
      props.whiteboard.unlinkObjects(objects, true)
      break
    case 'fullScreen':
      showDesignOptionFullscreen(activeObject as WbDesignOption)
      break
    case 'assignAsArticleImage':
      assignDesignImageAsArticleImage(activeObject as WbDesignOption)
      break

    default:
      throw new Error('Not Valid')
  }
}

function assignDesignImageAsArticleImage(activeObject: WbDesignOption) {
  if (userStore.activeCatalog && activeObject.article) {
    getAssetById(userStore.activeCatalog!.ContextKey, activeObject.assetId).then((asset) => {
      if (asset.data && asset.data.Status && asset.data.Key.toLowerCase() === 'design option' && userStore.activeCatalog) {
        const requestObj: CreateUpdateAssetModel = {
          Context: userStore.activeCatalog.DuneContext,
          ContextKey: userStore.activeCatalog.ContextReadWriteKey,
          ImageSet: activeObject.article!.ArticleNumber,
          Key: userStore.activeCatalog.Config.WhiteboardImagePlaceholderOptions.keys[0],
          SortOrder: userStore.activeCatalog.Config.WhiteboardImagePlaceholderOptions.sortOrder,
        }
        assignImageDialogRef.value?.showDialog(activeObject.article!, requestObj, true, asset.data)
      }
      else {
        notificationsStore.addNotification({ message: t('notifications.designOptionAlreadyAssigned'), type: 'Warning' })
      }
    }).catch(() => {
      notificationsStore.addNotification({ message: t('notifications.unableToAssignCurrently'), type: 'Warning' })
    })
  }
}

function showDesignOptionFullscreen(activeObject) {
  window.open(`${appConfig.AssetsUrl}/assets/content/${activeObject.assetStorageFile}?ContextKey=${encodeURIComponent(userStore.activeCatalog!.ContextKey)}&f=webp&trim=true`)
}

async function copySelectedToClipboard() {
  const activeObject = props.whiteboard!.canvas.getActiveObject()
  if (!activeObject) {
    notificationsStore.addNotification({ message: t('notifications.noActiveObject'), type: 'Warning' })
    return
  }
  const dataUrl = activeObject.toDataURL({
    format: 'png',
    multiplier: 1, // Higher quality
  })
  // Convert Data URL to Blob
  const blob = await fetch(dataUrl).then(res => res.blob())

  // Copy to clipboard
  try {
    await navigator.clipboard.write([
      new ClipboardItem({ 'image/png': blob }),
    ])
    notificationsStore.addNotification({ message: t('notifications.clonedSuccessfully'), type: 'Success' })
  }
  catch (err) {
    console.error('Failed to copy image:', err)
  }
  props.whiteboard?.canvas.discardActiveObject()
  props.whiteboard?.canvas.requestRenderAll()
}

function onAddModelDetails() {
  dropdownVisible.value = ''
  const activeObjects = props.whiteboard?.canvas.getActiveObjects()
  props.whiteboard?.canvas.discardActiveObject()
  const addedObjects: WbModelDetails[] = []
  const opt: any = { ...whiteboardStore.settings?.modelDetails || {} }
  activeObjects?.forEach(async (activeObject: any) => {
    let modelDetails: WbModelDetails | null = null
    if (activeObject.type === whiteboardConstants.objectTypes.modelImage) {
      opt.left = activeObject.aCoords.bl.x
      opt.top = activeObject.aCoords.bl.y
      opt.modelId = activeObject.modelId
      opt.article = activeObject.article
      const articleImageWidth = (activeObject.width || 1) * (activeObject.scaleX || 1)
      if (!Number.isNaN(articleImageWidth) && articleImageWidth !== 1) {
        opt.width = articleImageWidth
      }
      if (activeObject.modelId) {
        modelDetails = await WbModelDetails.loadModelDetailsByNumber(activeObject.catalogCode, opt)
      }
    }
    if (modelDetails) {
      // set article details object height otherwise it will take random height and while selection unnecessary white space is selected
      modelDetails.setProp('height', 52)
      props.whiteboard?.addObjects([modelDetails] as IWbObject[], true)
      addedObjects.push(modelDetails)
    }
  })
  if (activeObjects?.length && props.whiteboard) {
    const selection = new fabric.ActiveSelection(addedObjects, { canvas: props.whiteboard.canvas })
    props.whiteboard.canvas.setActiveObject(selection)
    props.whiteboard.canvas.requestRenderAll()
  }
}

function onAddArticleDetails() {
  dropdownVisible.value = ''
  const objs = props.whiteboard.canvas.getActiveObjects()
  props.whiteboard.canvas.discardActiveObject()
  const addedObjects: IWbObject[] = []
  const opt: any = { ...whiteboardStore.settings?.articleDetails || {} }
  objs?.forEach(async (o: any) => {
    let articleDetails: WbArticleDetails | null = null
    if (o.type === whiteboardConstants.objectTypes.articleImage) {
      opt.left = o.aCoords.bl.x
      opt.top = o.aCoords.bl.y
      opt.article = o.article
      const articleImageWidth = (o.width || 1) * (o.scaleX || 1)
      if (!Number.isNaN(articleImageWidth) && articleImageWidth !== 1) {
        opt.width = articleImageWidth
      }
      if (o.articleId) {
        articleDetails = await WbArticleDetails.loadArticleDetailsById(o.catalogCode, o.articleId, o.objectId, o.isRequest, opt)
      }
    }
    if (articleDetails) {
      // set article details object height otherwise it will take random height and while selection unneccesary white space is selected
      articleDetails.setProp('height', 52)
      props.whiteboard?.addObjects([articleDetails] as IWbObject[], true)
      addedObjects.push(articleDetails as IWbObject)
    }
  })
  if (objs?.length && props.whiteboard) {
    const selection = new fabric.ActiveSelection(addedObjects, { canvas: props.whiteboard.canvas })
    props.whiteboard.canvas.setActiveObject(selection)
    props.whiteboard.canvas.requestRenderAll()
  }
}

function onAssignImage() {
  assignImageRef.value?.click()
}

function onAddDiscussion() {
  dropdownVisible.value = ''
  const opt = { left: 0, top: 0 } as any
  const objs: any = props.whiteboard.canvas.getActiveObjects()
  if (objs.length !== 1 || !objs[0].id) {
    return
  }

  const o = objs[0] as IWbObject
  props.whiteboard.canvas.discardActiveObject()
  opt.left = (o.aCoords?.tr.x || 0) - 50
  opt.top = (o.aCoords?.tl.y || 0) + 50

  const discussionIcon = new WbDiscussionIcon(opt)

  if (o.canBeLinkedTo && o.id) {
    const oCoords = fabric.util.qrDecompose(o.calcTransformMatrix())
    const dCoords = fabric.util.qrDecompose(discussionIcon.calcTransformMatrix())
    let linkToId = o.id
    if ((o as any).dynamicGrid) {
      linkToId = ((o as any).dynamicGrid as WbDynamic).getChildObjectReference(o.id) || ''
      if (!linkToId || linkToId === '') {
        console.warn('Unable to resolve dynamic grid object', o.id)
        return
      }
    }

    discussionIcon.link = { id: linkToId, x: dCoords.translateX - oCoords.translateX, y: dCoords.translateY - oCoords.translateY }
  }

  props.whiteboard?.addObjects([discussionIcon] as IWbObject[], true)
  props.whiteboard?.canvas.setActiveObject(discussionIcon)
}

function onHideInactivePropChange(value: boolean) {
  const activeObject = props.whiteboard!.canvas.getActiveObject()

  if (activeObject === null) { return }

  form.hideInactive = value
  let objects
  if (activeObject.type === 'activeSelection') {
    objects = (activeObject as ActiveSelection).getObjects()
  }
  else {
    objects = [activeObject]
  }

  objects.forEach((obj) => {
    obj.setProp('hideInactive', value)
  })
}

function onAssetPropChange(asset: Record<string, string> | DuneAsset, objects: any[] = [], keepNewestFirstImageTypes: boolean = false) {
  let assetKey = asset.Id || asset.Key
  if (keepNewestFirstImageTypes) {
    assetKey = form.assetKey
  }

  const objs = props.whiteboard?.canvas.getActiveObjects()
  if (objects.length !== 0) {
    objs?.push(...objects)
  }
  objs?.forEach((o) => {
    (o as any).setProp('imageType', { assetKey }, true)
  })
  // hide/show the delete image action
  const obj = props.whiteboard?.canvas.getActiveObject() as WbArticleImage
  if (obj) {
    shouldShowDeleteImage(obj)
  }
  form.assetKey = assetKey
}

function shouldShowDeleteImage(obj: WbArticleImage) {
  const userStore = useUserStore()
  showDeleteImage.value = !!(
    userStore.userProfile.isValidPrivilege(privileges.duneImage.duneImageReadWritePrivilege)
    && obj.catalogCode === userStore.activeCatalog?.CatalogCode
    && userStore.activeCatalog?.Config.WhiteboardImagePlaceholderOptions.keys
    && userStore.activeCatalog?.Config.WhiteboardImagePlaceholderOptions.keys.length > 0
    && utils.isDefined(obj.assetKey)
    && userStore.activeCatalog?.Config.WhiteboardImagePlaceholderOptions.keys.includes(obj.assetKey)
  )
}

async function onDeleteImage() {
  dropdownVisible.value = ''
  const activeObject = props.whiteboard.canvas.getActiveObject() as WbArticleImage
  emit('deleteImage', activeObject)
}

function onInputChange(propName: string, propValue: any, formKey: string, formValue: any) {
  const objs = props.whiteboard?.canvas.getActiveObjects() as Array<IWbObject>
  if (objs.length) {
    if (objs.length === 1) {
      objs[0].setProp(propName, propValue)
    }
    else {
      objs.forEach((o) => {
        o.setProp(propName, propValue, true, false)
      })
      const sel = props.whiteboard.canvas.getActiveObject()
      if (sel) {
        props.whiteboard.canvas.fire('object:modified', { target: sel })
      }
    }
  }
  form[formKey] = formValue
}

function onAttributePropChange(attributes: string[]) {
  const objs = props.whiteboard?.canvas.getActiveObjects()
  objs?.forEach((o) => {
    (o as any).setProp('attributes', { attributes })
  })
}

function onThumbnailAttributePropChange(attributes: string[]) {
  const objs = props.whiteboard?.canvas.getActiveObjects()
  objs?.forEach((o) => {
    (o as any).setProp('attributes', { attributes })
  })
}

function onDeleteDiscussion() {
  const obj = props.whiteboard?.canvas.getActiveObject() as IWbObject
  if (obj) {
    props.whiteboard?.removeObjects([obj], true)
  }
}

function onAssignImageChange() {
  if (userStore.activeCatalog && assignImageRef.value?.files?.length && selectedObj.value) {
    const selectedArticleImage = selectedObj.value as WbArticleImage
    openAssignImageDialog(selectedArticleImage, assignImageRef.value.files[0])
    resetAssignImage()
  }
}

function openAssignImageDialog(articleImage: WbArticleImage, file?: File, assetToConvert?: DuneAsset) {
  if (userStore.activeCatalog && articleImage.article) {
    // as discussed with Andre, changing file name to ImageSet_Key
    const requestObj: CreateUpdateAssetModel = {
      Context: userStore.activeCatalog.DuneContext,
      ContextKey: userStore.activeCatalog.ContextReadWriteKey,
      ImageSet: articleImage.article.ArticleNumber,
      Key: userStore.activeCatalog.Config.WhiteboardImagePlaceholderOptions.keys[0],
      SortOrder: userStore.activeCatalog.Config.WhiteboardImagePlaceholderOptions.sortOrder,
    }
    if (file) {
      const filename = `${articleImage.article.ArticleNumber}_${userStore.activeCatalog.Config.WhiteboardImagePlaceholderOptions.keys[0]}.${file.name.split('.').pop()}`
      requestObj.file = new File([file], filename)
    }
    articleImageObjectForImageAssign = articleImage
    assignImageDialogRef.value?.showDialog(articleImage.article, requestObj, false, assetToConvert)
  }
}

function openDynamicPropertiesDialog(dynamicObj?: WbDynamic[], pos?: { x: number, y: number }) {
  currentDynamicObjs = dynamicObj
  currentObjPos = pos
  dynamicPropertiesRef.value?.showDialog(dynamicObj && dynamicObj.length > 0 ? dynamicObj[0].templateOptions || { templateId: '', options: {} } : { templateId: '', options: {} }, dynamicObj && dynamicObj.length > 0 ? dynamicObj[0].getFilter() : [])
}

function onAssignDynamicProperties(templateId: string, options: Record<string, DynamicTemplateOptionValue>, criteria: FilterCriteria[]) {
  if (currentDynamicObjs) {
    currentDynamicObjs.forEach((obj) => {
      obj.setProp('templateOptions', { id: templateId, options })
      obj.setProp('filter', criteria.map(c => c.toJSON()))
    })
  }
  else {
    const dynamic = new WbDynamic({ top: currentObjPos?.y, left: currentObjPos?.x, width: 1000, height: 1000, backgroundColor: 'transparent', fill: 'transparent', lockRotation: true, hasRotatingPoint: false, templateOptions: { templateId, options }, filterJson: criteria.map(c => c.toJSON()) })
    props.whiteboard?.addObjects([dynamic as IWbObject], true)
    nextTick(() => dynamic.repositionTitle())
  }
}

function resetAssignImage() {
  if (assignImageRef.value?.files?.length) {
    assignImageRef.value.files = null
    assignImageRef.value.value = ''
  }
}

function assignDesignOptionAsArticleImage(articleForRemove: MyArticle, articleToAdd: Article, assetId: string) {
  onAssignImageConfirm(articleForRemove, 'designOption')
  onAssignImageConfirm(articleToAdd, 'image', assetId)
}

async function onAssignImageConfirm(article: MyArticle | Article, source?: string, assetId?: string, isArticleUpdated?: boolean) {
  if (source && source === 'designOption') {
    const wbObjs = props.whiteboard?.canvas.getObjects() as any[]
    const targetObjects = [
      ...wbObjs.filter(obj => obj.type === whiteboardConstants.objectTypes.articleImage && obj.article && obj.article.ModelId === article.ModelId),
      ...wbObjs.filter(obj => obj.type === whiteboardConstants.objectTypes.modelImage && obj.article && obj.article.ModelId === article.ModelId),
      ...wbObjs.filter(obj => obj.type === whiteboardConstants.objectTypes.modelDetails && obj.article && obj.article.ModelId === article.ModelId),
    ]
    targetObjects.forEach((obj) => {
      const showDesignOptions = obj.showDesignOptions || false
      obj.setProp('showDesignOptions', { showDesignOptions })
    })
    const obj = props.whiteboard?.canvas.getActiveObject() as IWbObject
    if (obj?.type === whiteboardConstants.objectTypes.image || obj?.type === whiteboardConstants.objectTypes.dunesImage) {
      props.whiteboard?.removeObjects([obj], true)
    }
    emit('designOptionAdded', assetId!)
  }
  else if (userStore.activeCatalog && assetId && (userStore.userProfile.isValidPrivilege(privileges.article.allowBlockedArticleImages) || article.BlockImage === 0)) {
    const res = await utils.tryAsync(getArticleAssets(userStore.activeCatalog.DuneContext, userStore.activeCatalog.ContextReadKey, article.ArticleNumber, userStore.activeCatalog.Config.NewestImageAssetKeyList))
    if (res.success) {
      availableAssets.value = res.result
    }
    else {
      availableAssets.value = []
    }
    const wbObjs = props.whiteboard?.canvas.getObjects() as any[]
    let sameArticleObjects: WbArticleImage[] = wbObjs.filter(obj => obj.type === whiteboardConstants.objectTypes.articleImage && obj.articleId! && obj.articleId === article.Id)
    // from article toolbar action
    if (selectedObj.value) {
      selectedObj.value.assets = availableAssets.value
      sameArticleObjects = sameArticleObjects.filter(obj => obj.id !== selectedObj.value.id)
    }

    // by dropping image on article image
    if (selectedArticleImage) {
      const obj = props.whiteboard?.canvas.getActiveObject() as IWbObject
      if (obj?.type === whiteboardConstants.objectTypes.image || obj?.type === whiteboardConstants.objectTypes.dunesImage) {
        props.whiteboard?.removeObjects([obj], true)
      }
      selectedArticleImage.assets = availableAssets.value
      props.whiteboard?.canvas.setActiveObject(selectedArticleImage)
      const selectedArticleImageId = selectedArticleImage.id
      sameArticleObjects = sameArticleObjects.filter(obj => obj.id !== selectedArticleImageId)
    }
    sameArticleObjects.forEach((obj) => {
      obj.assets = availableAssets.value
    })
    // if current article image type(assetKey) is as same as userStore.activeCatalog.Config.WhiteboardImagePlaceholderOptions.key (then one uploaded from whiteboard) then update the article image after assigning new image
    if (!utils.isValidStringValue(form.assetKey) || (userStore.activeCatalog && userStore.activeCatalog.Config.WhiteboardImagePlaceholderOptions.keys.includes(form.assetKey)) || (form.assetKey === 'newestImage' || form.assetKey === 'firstImage')) {
      const selectedAsset = availableAssets.value.find(availableAsset => availableAsset.Id === assetId)
      let currentAsset = clone(selectedAsset)
      if (form.assetKey === 'firstImage') {
        utils.sort(availableAssets.value, ['SortOrder'])
        currentAsset = availableAssets.value[0]
      }
      else if (form.assetKey === 'newestImage') {
        currentAsset = availableAssets.value[0]
      }
      if (currentAsset && selectedAsset && currentAsset.Id === selectedAsset.Id) {
        const keepSameAssetKey = (form.assetKey === 'firstImage' || form.assetKey === 'newestImage')
        onAssetPropChange(selectedAsset, sameArticleObjects, keepSameAssetKey)
      }
    }
    // if article attributes updated with assign image refresh data
    if (isArticleUpdated && selectedArticleImage && selectedArticleImage.article) {
      emit('articleUpdated', [selectedArticleImage.article])
    }
    // add to history with undoHandler
    const currentArticleObject: WbArticleImage = wbObjs.filter(obj => obj.type === whiteboardConstants.objectTypes.articleImage && articleImageObjectForImageAssign.articleId! && obj.articleId === articleImageObjectForImageAssign.articleId)[0]
    props.whiteboard.addToHistory('assignImage', currentArticleObject, undefined, {
      undoHandler: async (object: WbArticleImage) => {
        const canDeleteAsset = userStore.userProfile.isValidPrivilege(privileges.duneImage.duneImageReadWritePrivilege)
          && object.catalogCode === userStore.activeCatalog?.CatalogCode
          && object.assetKey && userStore.activeCatalog?.Config.WhiteboardImagePlaceholderOptions.keys?.includes(object.assetKey)

        if (canDeleteAsset && userStore.activeCatalog) {
          const assetsToDelete: string[] = []
          const currentAsset = object.assets.find(asset => asset.Key === object.assetKey)
          if (currentAsset) {
            assetsToDelete.push(currentAsset.Id)
          }
          if (assetsToDelete.length > 0) {
            emit('deleteImage', object)
            object.dim()
          }
        }
      },
    })
  }
  else {
    // by dropping image on article image
    if (selectedArticleImage) {
      const obj = props.whiteboard?.canvas.getActiveObject() as IWbObject
      if (obj?.type === whiteboardConstants.objectTypes.image || obj?.type === whiteboardConstants.objectTypes.dunesImage) {
        props.whiteboard?.removeObjects([obj], true)
      }
    }
    await userStore.doLoadData(['Resources'])
  }
  resetSelectedArticleImage()
  resetAssignImage()
}

/**
 * @description: if there are any selectedArticleImage then un-highlight (dim) it and assign undefined to it
 */
function resetSelectedArticleImage() {
  if (selectedArticleImage) {
    selectedArticleImage.dim()
  }
  selectedArticleImage = undefined
}

function handlePanning(): void {
  panning.value = true
}

function reloadDeletedArticleImage() {
  // After deletion, update the imageType prop
  const obj = props.whiteboard?.canvas.getActiveObject() as WbArticleImage
  if (obj) {
    form.assetKey = obj.assetKey || ''
    availableAssets.value = obj.assets
    shouldShowDeleteImage(obj)
  }
}

defineExpose({
  openDynamicPropertiesDialog,
})
</script>
